import { Button, Typography, Link } from "@mui/material";
import { ReactNode, useContext, useMemo } from "react";
import { Contact, Job, Location } from "types";
import { canJobBeCancelled, formatLabel, getShortFormattedAddress } from "utils";
import Timeline from "@mui/lab/Timeline";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent, {
  timelineContentClasses,
} from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineOppositeContent, {
  timelineOppositeContentClasses,
} from "@mui/lab/TimelineOppositeContent";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { keyframes } from "@emotion/react";
import { CancelReason } from "api/jobs";
import { Cancel, CheckCircle } from "@mui/icons-material";
import { AppContext } from "context/appContext";

const pulseAnimation = keyframes`
  0% {
    transform: scale(0.9);
    box-shadow: 0 0 0 0 rgba(200, 0, 0, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(200, 0, 0, 0);
  }
  100% {
    transform: scale(0.9);
    box-shadow: 0 0 0 0 rgba(200, 0, 0, 0);
  }
`;

export interface StatusTimelineItem {
  status: string;
  displayTime: string;
  timestamp: number;
  reason?: string;
  location?: Location;
  contact?: Contact;
}

export interface JobStatusTimelineProps {
  job: Job | null;
  cancelReasons: CancelReason[];
  showCancelJobDialog: () => void;
}

const JobStatusTimeline = ({ job, cancelReasons, showCancelJobDialog }: JobStatusTimelineProps): ReactNode => {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);

  const canEdit = state.entityPermissions.job.update;

  const shouldShowCancelButton =
  !!state.tenant.data?.allowJobCancellationBeforeStatus &&
  state.features.allowJobCancellation &&
  canJobBeCancelled(job, state.tenant.data?.allowJobCancellationBeforeStatus);

  const isTerminalStatus = (status: string): boolean => {
    const terminalStatuses: string[] = ['complete', 'completed', 'cancelled', 'denied', 'approvaldenied'];
    return terminalStatuses.includes(String(status).replace(/[^a-zA-Z]/g, '').toLowerCase());
  };

  const cancelReason = useMemo(() => {
    if (!job?.data?.statuses) {
      return "";
    }
    const lastStatus = job.data.statuses[job.data.statuses.length - 1];
    return lastStatus.reason;
  }, [job?.data?.statuses]);

  const timelineData: StatusTimelineItem[] = useMemo(() => {
    const data: StatusTimelineItem[] = [];
    if (job?.data?.statuses) {
      job.data.statuses.forEach((status) => {
        const item: StatusTimelineItem = {
          status: status.status,
          displayTime: dayjs(new Date(status.timestamp * 1000)).format(
            "MM/DD/YYYY hh:mm A",
          ),
          timestamp: status.timestamp,
        };
        if (status.reason) {
          item.reason = status.reason;
        }
        if (status.location) {
          item.location = status.location;
        }
        if (status.contact) {
          item.contact = status.contact;
        }
        data.push(item);
      });
    }
    return data.sort((a, b) => a.timestamp - b.timestamp);
  }, [job?.data?.statuses]);

  return (
    <Timeline
      sx={{
        [`& .${timelineOppositeContentClasses.root}`]: {
          flex: 0.3,
        },
        [`& .${timelineContentClasses.root}`]: {
          flex: 0.7,
        },
      }}
    >
      <Typography variant="h6" sx={{ ml: { xs: -2, md: 0 }, mb: { xs: 0, md: 2 } }}>
        {t("labels.live_status", { ns: "jobs" })}
      </Typography>
      {timelineData.map((item, index) => {
        return (
          <TimelineItem key={item.status + index}>
            <TimelineOppositeContent>
              <Typography component="span">
                {formatLabel(item.status)}
              </Typography>
            </TimelineOppositeContent>
            <TimelineSeparator>
            {isTerminalStatus(item.status) ? (
                <TimelineDot
                  variant={"outlined"}
                  color="error"
                />
              ) : (
                <TimelineDot
                sx={
                  item.status === job?.status
                    ? {
                        animation: `${pulseAnimation} 1.5s infinite`,
                        animationDelay: `${index * 0.5}s`,
                      }
                    : {}
                }
                variant={"filled"}
                color="error"
              />
              )}
{!isTerminalStatus(item.status) && (
  <TimelineConnector />
)}
            </TimelineSeparator>
            <TimelineContent>
              <Typography variant={"body2"}>{item.displayTime}</Typography>
              {item?.location && (
                <Typography variant={"body2"}>
                  <Link
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      getShortFormattedAddress(item?.location),
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="subtitle2"
                    fontWeight={"bold"}
                  >
                    {getShortFormattedAddress(item?.location)}
                  </Link>
                </Typography>
              )}
            </TimelineContent>
          </TimelineItem>
        );
      })}              
      {cancelReason && (
        <Typography variant={"body2"} sx={{ ml: 4 }}>
          {t("labels.cancellation_reason", { ns: "jobs" })}: {cancelReason}
        </Typography>
      )}
      {shouldShowCancelButton && (
        <Button
          onClick={showCancelJobDialog}
          disabled={!canEdit || cancelReasons.length === 0}
          variant='outlined'
          color='error'
          startIcon={<Cancel />}
          sx={{
            borderRadius: 1,
            px: 2,
            py: 1,
            mx: 3,
            width: 270,
          }}
        >
          {t('dialogs.cancel.confirm', { ns: 'jobs' })}
        </Button>
      )}
    </Timeline>
  );
};

export default JobStatusTimeline;
